<script lang="ts">
	// T25: Printer setup sheet (contained Sheet).
	//
	// Flow:
	// Mode 'add' | 'again' (printer given) | 'change' (printer given).
	// add/again:
	//   Step 1: address field (`printer-setup-address`, mono, digits and dots only)
	//           with its note and error under it.
	//           Sheet primary: "Print test slip" (`printer-setup-test`),
	//           disabled when address is empty, pendingLabel while printing.
	//   Step 2: Sheet Back labelled with the address word that returns to step 1.
	//           Typed address, large code in mono (`printer-setup-code`), question,
	//           Yes (`printer-setup-code-yes`, primary, lg, check icon)
	//           and No (`printer-setup-code-no`, secondary, lg).
	//           Answering No shows a flag Band and two lg buttons:
	//           Try again (`printer-setup-retry`, primary, printer icon)
	//           and Change the address (`printer-setup-change-address`, secondary).
	//           Sheet has no primary on this step.
	//   'again': first step explains that the printer keeps its name and what it prints;
	//           Yes on the code saves at once (PATCH address) and closes.
	// Step 3 (add only; change opens on it):
	//   Two-column grid:
	//     Left: address line, Name Field (`printer-setup-name`, note and error),
	//           Paper Segmented 80/58 (`printer-setup-paper`, default 80),
	//           Drawer Segmented Yes/No (`printer-setup-drawer`, shown only when receipts picked;
	//           reset to No when receipts unpicked).
	//     Right: Choice in multi mode stacked (`printer-setup-purpose`),
	//           options receipts and kitchen; purpose error under it.
	//     Sheet primary: Save (`printer-setup-save`, check icon, disabled until purpose picked).
	//
	// A FORBIDDEN refusal is a flag Band with role alert at top of sheet.
	//
	// On a till with its own setup (#522) the steps stay the same, and the
	// store's mesh does the work: Print test slip first finds the printer at
	// the typed address, then prints the slip on the printer found; Save sends
	// the whole setup, with the found printer for a new printer or a new
	// address. A stale refusal is a flag Band at the top, and the list reads
	// again.
	//
	// On a till that lists windows_usb or android_usb (#523, #516, #622), step 1
	// also lists the printers plugged into the till that are not set up yet,
	// of either kind, as "USB printer on this till: {name}" ({name} the Windows
	// queue's name or the Android printer's model), in one stacked Choice
	// above the address field (`printer-setup-usb`). Picking one takes the
	// place of the address: Print test slip prints on it, the code step says
	// the USB printer on this till printed it, and the details name it where
	// they name an address. On Android a line under the field says Android
	// must allow Bosla first, and the list is read again while the step shows.

	import { untrack } from 'svelte';
	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Choice from '../components/Choice.svelte';
	import Field from '../components/Field.svelte';
	import Segmented from '../components/Segmented.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { fill, message, type Language } from '../i18n/messages';
	import {
		ANDROID_USB,
		addPrinter,
		attachedName,
		changePrinter,
		findAttachedPrinters,
		findsAttached,
		meshAttachedName,
		findPrinter,
		meshCapabilities,
		meshSetup,
		requestMeshTestSlip,
		requestTestSlip,
		saveLocalSetup,
		watchJob,
		type FoundPrinter,
		type Printer,
		type Refusal,
		type SlipOutcome,
	} from '../till/printers.svelte';

	interface Props {
		open: boolean;
		language: Language;
		mode: 'add' | 'again' | 'change';
		printer?: Printer | null;
		onClose: () => void;
		onSaved: () => void;
	}

	let { open, language, mode, printer = null, onClose, onSaved }: Props = $props();

	let step = $state<1 | 2 | 3>(1);
	let address = $state('');
	let testedAddress = $state('');
	let testCode = $state('');
	let testPrinting = $state(false);
	let testFailReason = $state('');
	let answeredNo = $state(false);

	let name = $state('');
	let purposes = $state<('receipts' | 'kitchen')[]>([]);
	let paper = $state<'80' | '58'>('80');
	let hasDrawer = $state(false);

	let saving = $state(false);
	let refusal = $state<Refusal | null>(null);

	// The till's own setup, as the mesh said when the sheet opened, and the
	// printer found at the address the test slip proved.
	let local = $state(false);
	let foundId = $state('');

	// The printers plugged into this till (#523, #516), the one picked in place
	// of an address, and the name of the one the slip went to, if any.
	let attached = $state<FoundPrinter[]>([]);
	let pickedUsb = $state('');
	let testedUsb = $state<string | null>(null);
	let finding = 0;
	const findsUsb = $derived(local && mode !== 'change' && findsAttached(meshCapabilities()));
	// Android lets Bosla use a printer only once the person allows it there.
	const androidUsb = $derived(findsUsb && meshCapabilities().includes(ANDROID_USB));

	async function findAttached() {
		const ask = ++finding;
		const outcome = await findAttachedPrinters();
		if (ask !== finding) return;
		// A refused find lists nothing, so no printer that went away stays offered.
		attached = outcome.kind === 'found' ? outcome.printers : [];
		if (pickedUsb && !attached.some((p) => p.id === pickedUsb)) pickedUsb = '';
	}

	// The name a printer set up on a USB printer shows where another shows its
	// address: the one the slip went to, or the one a printer changed is on.
	const usbName = $derived.by(() => {
		if (testedUsb !== null) return testedUsb;
		if (mode === 'change' && printer && !testedAddress) return meshAttachedName(printer.id);
		return null;
	});

	function usbLabel(name: string): string {
		return name
			? fill(message(language, 'printerUsbOnThisTill'), name)
			: message(language, 'printerUsbOptionAnon');
	}

	// A printer already set up is not offered again, but the one found again
	// is offered at its own place (#518).
	const usbOptions = $derived(
		attached
			.filter((found) => !found.sinkId || (mode === 'again' && found.sinkId === printer?.id))
			.map((found) => ({
				value: found.id,
				label: usbLabel(attachedName(found)),
				testId: `printer-setup-usb-${found.id}`,
			})),
	);

	let slipAbort: AbortController | null = null;

	// A watch of a test slip ends with the sheet, even when the sheet goes away open.
	$effect(() => () => slipAbort?.abort());

	$effect(() => {
		if (open) {
			refusal = null;
			local = untrack(meshSetup) === 'till';
			foundId = '';
			pickedUsb = '';
			attached = [];
			finding++;
			testedUsb = null;
			testedAddress = '';
			testPrinting = false;
			testFailReason = '';
			answeredNo = false;
			if (mode === 'change' && printer) {
				step = 3;
				address = printer.address ?? '';
				testedAddress = printer.address ?? '';
				name = printer.name;
				purposes = [...printer.purposes];
				paper = printer.paperWidthMm === 58 ? '58' : '80';
				hasDrawer = printer.hasDrawer;
			} else if (mode === 'again' && printer) {
				step = 1;
				address = '';
				name = printer.name;
				purposes = [...printer.purposes];
				paper = printer.paperWidthMm === 58 ? '58' : '80';
				hasDrawer = printer.hasDrawer;
			} else {
				step = 1;
				address = '';
				name = '';
				purposes = [];
				paper = '80';
				hasDrawer = false;
				testCode = '';
			}
		} else {
			slipAbort?.abort();
			slipAbort = null;
		}
	});

	// The printers plugged into the till are found while the first step shows,
	// after the sheet's reset above; Android may allow one while the sheet is
	// open, so there the list is read again now and then.
	$effect(() => {
		if (!open || step !== 1 || !findsUsb) return;
		void findAttached();
		if (!androidUsb) return;
		const timer = setInterval(() => void findAttached(), 4000);
		return () => clearInterval(timer);
	});

	const title = $derived.by(() => {
		if (mode === 'change' && printer) {
			return fill(message(language, 'printerChangeTitle'), printer.name);
		}
		if (mode === 'again' && printer) {
			return fill(message(language, 'printerSetUpAgainTitle'), printer.name);
		}
		return message(language, 'printersAdd');
	});

	async function handleTestSlip() {
		if ((!address.trim() && !pickedUsb) || testPrinting) return;
		testPrinting = true;
		testFailReason = '';
		refusal = null;
		slipAbort?.abort();
		const controller = new AbortController();
		slipAbort = controller;

		const usb = attached.find((c) => c.id === pickedUsb);
		const sentAddress = usb ? usb.locator : address.trim();
		try {
			let outcome: SlipOutcome;
			if (local && usb) {
				// A printer plugged into this till, found already.
				foundId = usb.id;
				outcome = await requestMeshTestSlip(usb.id);
			} else if (local) {
				const found = await findPrinter(sentAddress);
				if (controller.signal.aborted) return;
				if (found.kind === 'found') {
					foundId = found.printer.id;
					outcome = await requestMeshTestSlip(found.printer.id);
				} else {
					outcome = found;
				}
			} else {
				outcome = await requestTestSlip(sentAddress);
			}
			if (controller.signal.aborted) return;
			if (outcome.kind === 'refused') {
				refusal = outcome;
				testPrinting = false;
				return;
			}
			testedAddress = sentAddress;
			testedUsb = usb ? attachedName(usb) : null;
			const job = outcome.job;
			testCode = job.code ?? '';

			const result = await watchJob(job.id, controller.signal);
			if (result.kind === 'printed') {
				testPrinting = false;
				step = 2;
				answeredNo = false;
			} else if (result.kind === 'failed') {
				testPrinting = false;
				testFailReason = result.failReason ?? 'unreachable';
				step = 1;
				answeredNo = false;
			} else {
				testPrinting = false;
			}
		} catch {
			testPrinting = false;
			refusal = { kind: 'refused', code: 'failed' };
		}
	}

	function handleBackToAddress() {
		step = 1;
		answeredNo = false;
		testFailReason = '';
	}

	async function handleRetryTestSlip() {
		answeredNo = false;
		await handleTestSlip();
	}

	async function handleCodeYes() {
		if (saving) return;
		if (mode === 'again' && printer) {
			saving = true;
			refusal = null;
			const outcome = local
				? await saveLocalSetup({ kind: 'change', id: printer.id, candidateId: foundId })
				: await changePrinter(printer.id, { address: testedAddress });
			saving = false;
			if (outcome.kind === 'saved') {
				onSaved();
			} else {
				refusal = outcome;
				if (outcome.code === 'already_added' || outcome.code === 'invalid_address') {
					step = 1;
				}
			}
		} else {
			step = 3;
		}
	}

	async function handleSave() {
		if (saving || purposes.length === 0 || !name.trim()) return;
		saving = true;
		refusal = null;
		const paperWidthMm = paper === '58' ? 58 : 80;
		const drawer = purposes.includes('receipts') && hasDrawer;

		if (mode === 'add') {
			const fields = { name: name.trim(), purposes, paperWidthMm, hasDrawer: drawer } as const;
			const outcome = local
				? await saveLocalSetup({ kind: 'add', candidateId: foundId, ...fields })
				: await addPrinter({ address: testedAddress, ...fields });
			saving = false;
			if (outcome.kind === 'saved') {
				onSaved();
			} else {
				refusal = outcome;
				// The address is on step 1: show its refusal under the field it belongs to.
				if (outcome.code === 'already_added' || outcome.code === 'invalid_address') step = 1;
			}
		} else if (mode === 'change' && printer) {
			const fields = { name: name.trim(), purposes, paperWidthMm, hasDrawer: drawer } as const;
			const outcome = local
				? await saveLocalSetup({ kind: 'change', id: printer.id, ...fields })
				: await changePrinter(printer.id, fields);
			saving = false;
			if (outcome.kind === 'saved') {
				onSaved();
			} else {
				refusal = outcome;
			}
		}
	}

	const addressError = $derived.by(() => {
		if (testFailReason) {
			return testFailReason === 'other_printer'
				? message(language, 'printerSlipOtherPrinter')
				: message(language, 'printerSlipUnreachable');
		}
		if (!refusal) return '';
		if (refusal.code === 'already_added') {
			return refusal.name
				? fill(message(language, 'printerAlreadyAdded'), refusal.name)
				: message(language, 'printerAlreadyAddedAnon');
		}
		if (refusal.code === 'invalid_address') {
			return message(language, 'printerCheckAddress');
		}
		// A Windows till's print host is starting (#524): finding waits for it.
		if (refusal.code === 'print_host_unavailable') {
			return message(language, 'printerHostNotReady');
		}
		return '';
	});

	const nameError = $derived.by(() => {
		if (!refusal) return '';
		if (refusal.code === 'name_in_use') {
			return message(language, 'printerNameInUse');
		}
		if (refusal.code === 'invalid_name') {
			return message(language, 'printerNameHint');
		}
		return '';
	});

	const purposeError = $derived.by(() => {
		if (!refusal || refusal.code !== 'purpose_taken') return '';
		const what =
			refusal.purpose === 'kitchen'
				? message(language, 'printerPurposeKitchen')
				: message(language, 'printerPurposeReceipts');
		return refusal.name
			? fill(message(language, 'printerPurposeTaken'), refusal.name, what)
			: fill(message(language, 'printerPurposeTakenAnon'), what);
	});

	const paperOptions = $derived([
		{
			value: '80',
			label: message(language, 'printerPaper80'),
			testId: 'printer-setup-paper-80',
		},
		{
			value: '58',
			label: message(language, 'printerPaper58'),
			testId: 'printer-setup-paper-58',
		},
	]);

	const drawerOptions = $derived([
		{
			value: 'yes',
			label: message(language, 'printerYes'),
			testId: 'printer-setup-drawer-yes',
		},
		{
			value: 'no',
			label: message(language, 'printerNo'),
			testId: 'printer-setup-drawer-no',
		},
	]);

	const purposeOptions = $derived([
		{
			value: 'receipts',
			label: message(language, 'printerPrintsReceipts'),
			testId: 'printer-setup-purpose-receipts',
		},
		{
			value: 'kitchen',
			label: message(language, 'printerPrintsKitchen'),
			testId: 'printer-setup-purpose-kitchen',
		},
	]);
</script>

<Sheet
	{open}
	contain
	level={1}
	{title}
	titleTestId="printer-setup-title"
	backLabel={step !== 1 && mode !== 'change' ? message(language, 'printerBackAddress') : undefined}
	onBack={step !== 1 && mode !== 'change' ? handleBackToAddress : undefined}
	backTestId="printer-setup-back"
	primaryLabel={step === 1
		? message(language, 'printerTestSlip')
		: step === 3
			? message(language, 'printerSave')
			: undefined}
	primaryIcon={step === 3 ? 'check' : undefined}
	primaryTestId={step === 1 ? 'printer-setup-test' : 'printer-setup-save'}
	primaryDisabled={step === 1
		? testPrinting || (!address.trim() && !pickedUsb)
		: saving || purposes.length === 0 || !name.trim()}
	primaryState={step === 1 ? (testPrinting ? 'pending' : 'idle') : saving ? 'pending' : 'idle'}
	pendingLabel={step === 1
		? message(language, 'printerTestSlipPrinting')
		: message(language, 'saving')}
	onPrimary={step === 1 ? handleTestSlip : step === 3 ? handleSave : undefined}
	{onClose}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="printer-setup-close"
	testId="printer-setup-sheet"
>
	<div class="sheet-body" data-scroll="">
		{#if refusal?.code === 'forbidden'}
			<Band tone="flag" alert testId="printer-setup-error">
				{message(language, 'printerForbidden')}
			</Band>
		{:else if refusal?.code === 'stale'}
			<Band tone="flag" alert testId="printer-setup-error">
				{message(language, 'printerSetupStale')}
			</Band>
		{:else if refusal && (refusal.code === 'failed' || refusal.code === 'invalid' || refusal.code === 'print_in_progress')}
			<Band tone="flag" testId="printer-setup-error">
				{message(language, 'printerFailed')}
			</Band>
		{/if}

		{#if step === 1}
			<div class="step-addr">
				{#if mode === 'again'}
					<p class="keeps-text">{message(language, 'printerKeepsName')}</p>
				{/if}
				{#if usbOptions.length > 0}
					<Choice
						testId="printer-setup-usb"
						title={message(language, 'printerUsbPick')}
						options={usbOptions}
						value={pickedUsb}
						stack
						onChange={(val) => {
							if (testPrinting) return;
							pickedUsb = val as string;
							address = '';
							testFailReason = '';
						}}
					/>
					<p class="keeps-text">{message(language, 'printerUsbOrAddress')}</p>
				{/if}
				<Field
					testId="printer-setup-address"
					label={message(language, 'printerAddressLabel')}
					note={message(language, 'printerAddressNote')}
					noteTestId="printer-setup-address-hint"
					error={addressError}
					errorTestId="printer-setup-address-error"
					value={address}
					onInput={(val) => {
						if (testPrinting) return;
						address = val.replace(/[^0-9.]/g, '');
						if (address) pickedUsb = '';
						if (refusal?.code === 'already_added' || refusal?.code === 'invalid_address') {
							refusal = null;
						}
						testFailReason = '';
					}}
					inputMode="decimal"
					mono
					maxLength={15}
					placeholder="192.168.1.60"
				/>
				{#if androidUsb}
					<p class="usb-help" data-testid="printer-setup-usb-help">
						{message(language, 'printerUsbHelp')}
					</p>
				{/if}
			</div>
		{:else if step === 2}
			<div class="step-code">
				<p class="code-intro">
					{#if testedUsb !== null}
						{message(language, 'printerCodeIntroUsb')}
					{:else}
						{message(language, 'printerCodeIntro').split('%1')[0]}<bdi dir="ltr" class="mono"
							>{testedAddress}</bdi
						>{message(language, 'printerCodeIntro').split('%1')[1] ?? ''}
					{/if}
				</p>
				<span dir="ltr" class="code-large" data-testid="printer-setup-code">
					{testCode}
				</span>
				{#if !answeredNo}
					<p class="code-question">{message(language, 'printerCodeQuestion')}</p>
					<div class="choice-actions">
						<Button
							testId="printer-setup-code-yes"
							icon="check"
							label={message(language, 'printerYes')}
							tone="primary"
							size="lg"
							disabled={saving}
							state={saving ? 'pending' : 'idle'}
							pendingLabel={message(language, 'saving')}
							onPress={handleCodeYes}
						/>
						<Button
							testId="printer-setup-code-no"
							label={message(language, 'printerNo')}
							tone="secondary"
							size="lg"
							disabled={saving}
							onPress={() => (answeredNo = true)}
						/>
					</div>
				{:else}
					<Band tone="flag">
						{message(language, 'printerCodeNotPrinted')}
					</Band>
					<div class="choice-actions">
						<Button
							testId="printer-setup-retry"
							icon="printer"
							label={message(language, 'printerTryAgain')}
							tone="primary"
							size="lg"
							disabled={testPrinting}
							state={testPrinting ? 'pending' : 'idle'}
							pendingLabel={message(language, 'printerTestSlipPrinting')}
							onPress={handleRetryTestSlip}
						/>
						<Button
							testId="printer-setup-change-address"
							label={message(language, 'printerChangeAddress')}
							tone="secondary"
							size="lg"
							onPress={handleBackToAddress}
						/>
					</div>
				{/if}
			</div>
		{:else if step === 3}
			<div class="details-grid">
				<div class="details-left">
					<p class="details-at">
						{#if usbName !== null}
							{usbLabel(usbName)}
						{:else}
							{message(language, 'printerAtAddress').split('%1')[0]}<bdi dir="ltr" class="mono"
								>{testedAddress}</bdi
							>{message(language, 'printerAtAddress').split('%1')[1] ?? ''}
						{/if}
					</p>
					<Field
						testId="printer-setup-name"
						label={message(language, 'printerNameLabel')}
						note={message(language, 'printerNameNote')}
						noteTestId="printer-setup-name-hint"
						error={nameError}
						errorTestId="printer-setup-name-error"
						value={name}
						onInput={(val) => {
							name = val;
							if (refusal?.code === 'name_in_use' || refusal?.code === 'invalid_name') {
								refusal = null;
							}
						}}
						maxLength={40}
					/>
					<div class="field-stack">
						<span class="group-label">{message(language, 'printerPaperLabel')}</span>
						<Segmented
							testId="printer-setup-paper"
							label={message(language, 'printerPaperLabel')}
							options={paperOptions}
							value={paper}
							onChange={(val) => (paper = val as '80' | '58')}
						/>
					</div>
					{#if purposes.includes('receipts')}
						<div class="field-stack">
							<span class="group-label">{message(language, 'printerDrawerOn')}</span>
							<Segmented
								testId="printer-setup-drawer"
								label={message(language, 'printerDrawerOn')}
								options={drawerOptions}
								value={hasDrawer ? 'yes' : 'no'}
								onChange={(val) => (hasDrawer = val === 'yes')}
							/>
						</div>
					{/if}
				</div>
				<div class="details-right">
					<Choice
						testId="printer-setup-purpose"
						title={message(language, 'printerPrintsLabel')}
						mode="multi"
						stack
						options={purposeOptions}
						value={purposes}
						onChange={(val) => {
							purposes = val as ('receipts' | 'kitchen')[];
							if (!purposes.includes('receipts')) {
								hasDrawer = false;
							}
							if (refusal?.code === 'purpose_taken') {
								refusal = null;
							}
						}}
					/>
					{#if purposeError}
						<p class="error-line" role="alert" data-testid="printer-setup-purpose-error">
							{purposeError}
						</p>
					{/if}
				</div>
			</div>
		{/if}
	</div>
</Sheet>

<style>
	.sheet-body {
		flex: 1;
		min-block-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		overflow-y: auto;
		overflow-x: hidden;
	}

	.step-addr {
		max-inline-size: var(--measure);
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.usb-help {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}

	.step-code {
		max-inline-size: var(--measure);
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-7);
	}

	.keeps-text,
	.code-intro {
		margin: 0;
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.code-large {
		font-family: var(--font-mono);
		font-size: var(--figure-xl);
		font-weight: 700;
		line-height: 1;
		letter-spacing: var(--tracking-tight);
		color: var(--text-strong);
	}

	.code-question {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 700;
		color: var(--text-strong);
	}

	.choice-actions {
		display: flex;
		gap: var(--touch-gap);
		flex-wrap: wrap;
	}

	.details-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
		gap: var(--space-9);
		align-items: start;
	}

	.details-left {
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.details-at {
		margin: 0;
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.field-stack {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.details-right {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.group-label {
		margin: 0;
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.error-line {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		font-weight: 600;
		color: var(--ink-flag-deep);
	}

	.mono {
		font-family: var(--font-mono);
	}
</style>
